/**
 * ProfileBadge - Clickable profile display with avatar and name
 * Apple HIG–inspired: no pill background, clean inline layout,
 * hover communicated via subtle opacity shift.
 */

import { startTransition } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { storage } from "@/lib/storage";
import { cn } from "@/lib/utils/general";

// Preload page components to avoid Suspense flash during navigation
const preloadStudioHandlePage = () => {
  import("@/pages/studio-handle-page");
};
const preloadProfilePage = () => {
  import("@/pages/renter/profile");
};

interface ProfileBadgeProps {
  /** Profile username for navigation - if missing, badge is not clickable */
  username?: string | null;
  /** Display name - falls back to username if missing */
  fullName?: string | null;
  /** Profile photo path or URL */
  profilePhoto?: string | null;
  /** Optional suffix to display after the name (e.g., "(Sub)") */
  suffix?: string;
  /** Optional className for additional styling */
  className?: string;
  /** Whether to show the avatar - defaults to true */
  showAvatar?: boolean;
  /** Visual variant - "light" for overlay on dark backgrounds (photos), "on-light" for page gradient backgrounds */
  variant?: "default" | "light" | "on-light";
}

function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

const textColor = {
  default: "text-on-dark-muted",
  light: "text-white/90",
  "on-light": "text-on-light-muted",
} as const;

const textHover = {
  default: "group-hover/badge:text-on-dark",
  light: "group-hover/badge:text-white",
  "on-light": "group-hover/badge:text-on-light",
} as const;

const fallbackStyle = {
  default: "bg-muted text-on-dark-muted",
  light: "bg-white/20 text-white",
  "on-light": "bg-on-light/10 text-on-light-muted",
} as const;

export function ProfileBadge({
  username,
  fullName,
  profilePhoto,
  suffix,
  className = "",
  showAvatar = true,
  variant = "default",
}: ProfileBadgeProps) {
  const navigate = useNavigate();
  const displayName = fullName || username || "Unknown";
  const displayText = suffix ? `${displayName} ${suffix}` : displayName;

  // Get photo URL - handle both storage paths and full URLs
  const photoUrl = profilePhoto
    ? profilePhoto.startsWith("http")
      ? profilePhoto
      : storage.getThumbnailUrl(profilePhoto, {
          width: 56,
          height: 56,
          quality: 80,
        })
    : null;

  const avatar = showAvatar ? (
    <Avatar className="h-6 w-6 shrink-0">
      {photoUrl && <AvatarImage src={photoUrl} alt={displayName} />}
      <AvatarFallback className={cn("text-[10px]", fallbackStyle[variant])}>
        {getInitials(displayName)}
      </AvatarFallback>
    </Avatar>
  ) : null;

  const nameEl = (
    <span
      className={cn(
        "text-sm font-medium transition-colors duration-150",
        textColor[variant],
        username && textHover[variant],
      )}
    >
      {displayText}
    </span>
  );

  // Non-clickable: no username
  if (!username) {
    if (!showAvatar) {
      return (
        <span className={cn("inline-block py-0.5 text-sm font-medium", textColor[variant], className)}>
          {displayText}
        </span>
      );
    }
    return (
      <div className={cn("inline-flex items-center gap-2 py-0.5", className)}>
        {avatar}
        {nameEl}
      </div>
    );
  }

  // Preload on hover/focus
  const handlePreload = () => {
    preloadStudioHandlePage();
    preloadProfilePage();
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handlePreload();
    startTransition(() => {
      navigate(`/${username}`);
    });
  };

  // Text-only clickable
  if (!showAvatar) {
    return (
      <a
        href={`/${username}`}
        className={cn(
          "group/badge inline-block py-0.5 text-sm font-medium transition-colors duration-150 cursor-pointer",
          textColor[variant],
          textHover[variant],
          className,
        )}
        onClick={handleClick}
        onMouseEnter={handlePreload}
        onFocus={handlePreload}
      >
        {displayText}
      </a>
    );
  }

  // Avatar + name, clickable
  return (
    <a
      href={`/${username}`}
      className={cn(
        "group/badge inline-flex items-center gap-2 py-0.5 rounded-lg transition-opacity duration-150 cursor-pointer hover:opacity-80 active:opacity-60",
        className,
      )}
      onClick={handleClick}
      onMouseEnter={handlePreload}
      onFocus={handlePreload}
    >
      {avatar}
      {nameEl}
    </a>
  );
}
